import { EligibleFilesEmptyState } from "@/components/reconciliation/empty-states";
import { TruncatedText } from "@/components/ui/truncation-tooltip";
import { getEligibleFiles } from "@/services/api";
import { formatMessage, reconciliationMessages, uiMessages } from "@/services/messages";
import { type FileRef, formatFileSheetLabel } from "@efes/shared";
import { useQuery } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Loader2 } from "lucide-react";
import { useMemo, useRef, useState } from "react";

interface EligibleFilesTableProps {
	fileRole: "work_order" | "act";
	selectedFileIds: number[];
	onChange: (fileIds: number[]) => void;
}

const ROW_HEIGHT = 44;
/** Высота секции для empty/no-search-results состояний. */
const MIN_TABLE_HEIGHT = 96;
/** Кэп: за этой высотой включается виртуализированный скролл. */
const MAX_TABLE_HEIGHT = 400;

function formatPeriod(month: number | null, year: number | null): string {
	if (!month && !year) return "—";
	const m = month ? String(month).padStart(2, "0") : "??";
	const y = year ?? "????";
	return `${m}/${y}`;
}

function formatDate(value: string): string {
	const parsed = new Date(value);
	if (Number.isNaN(parsed.getTime())) return value;
	return parsed.toLocaleString("ru-RU", {
		day: "2-digit",
		month: "2-digit",
		year: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

/**
 * Таблица eligible-файлов с виртуальным скроллом, поиском и чекбоксами.
 *
 * - Загружает файлы через `getEligibleFiles(role)` + TanStack Query.
 * - Empty state по FR-019b — `<EligibleFilesEmptyState role>` с deeplink'ом.
 * - Виртуальный скролл `@tanstack/react-virtual` (≥ 1000 строк без проседания).
 * - Поиск клиент-side по `originalName` и `sheetName` (case-insensitive includes).
 */
export function EligibleFilesTable({
	fileRole,
	selectedFileIds,
	onChange,
}: EligibleFilesTableProps) {
	const role = fileRole;
	const [search, setSearch] = useState("");
	const parentRef = useRef<HTMLDivElement>(null);

	const { data, isLoading } = useQuery({
		queryKey: ["eligible-files", role],
		queryFn: () => getEligibleFiles(role),
	});

	const allFiles = data?.files ?? [];
	const total = data?.total ?? 0;

	const filteredFiles = useMemo(() => {
		const q = search.trim().toLowerCase();
		if (!q) return allFiles;
		return allFiles.filter(
			(file) =>
				file.originalName.toLowerCase().includes(q) ||
				(file.sheetName?.toLowerCase().includes(q) ?? false),
		);
	}, [allFiles, search]);

	const selectedSet = useMemo(() => new Set(selectedFileIds), [selectedFileIds]);

	// L5: O(N) подсчёт выбранных среди отфильтрованных видимых файлов вместо
	// прежнего O(N×M) `selectedFileIds.filter(...allFiles.some(...))`.
	const visibleSelectedCount = useMemo(
		() => filteredFiles.filter((f) => selectedSet.has(f.id)).length,
		[filteredFiles, selectedSet],
	);

	const virtualizer = useVirtualizer({
		count: filteredFiles.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => ROW_HEIGHT,
		overscan: 8,
	});

	const toggleFile = (fileId: number) => {
		const next = new Set(selectedSet);
		if (next.has(fileId)) {
			next.delete(fileId);
		} else {
			next.add(fileId);
		}
		onChange(Array.from(next));
	};

	const allFilteredSelected =
		filteredFiles.length > 0 && filteredFiles.every((f) => selectedSet.has(f.id));

	const toggleAll = () => {
		if (allFilteredSelected) {
			// Снимаем выделение только с отфильтрованных
			const filteredIds = new Set(filteredFiles.map((f) => f.id));
			onChange(selectedFileIds.filter((id) => !filteredIds.has(id)));
		} else {
			const next = new Set(selectedFileIds);
			for (const f of filteredFiles) next.add(f.id);
			onChange(Array.from(next));
		}
	};

	if (isLoading) {
		return (
			<div className="flex items-center justify-center rounded-lg border border-slate-200 bg-white py-12 text-sm text-slate-500">
				<Loader2 className="mr-2 h-4 w-4 animate-spin" /> {reconciliationMessages.loading}
			</div>
		);
	}

	if (total === 0) {
		return <EligibleFilesEmptyState fileRole={role} />;
	}

	const items = virtualizer.getVirtualItems();

	// Динамическая высота: подгоняемся под реальное количество файлов.
	// Для пустого результата (search не нашёл) — минимум, чтобы поместилось
	// сообщение «нет результатов». Иначе — exact-fit, но не выше MAX.
	const tableHeight =
		filteredFiles.length === 0
			? MIN_TABLE_HEIGHT
			: Math.min(MAX_TABLE_HEIGHT, filteredFiles.length * ROW_HEIGHT);

	return (
		<div className="space-y-2">
			<input
				type="search"
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				placeholder={reconciliationMessages.form_search_placeholder}
				className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
			/>

			<div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
				{/* Заголовок таблицы */}
				<div className="grid grid-cols-[40px_minmax(0,5fr)_7rem_minmax(0,2fr)_120px_160px] items-center gap-x-4 border-b border-slate-200 bg-slate-50 px-3 py-2 text-xs font-medium uppercase tracking-wide text-slate-500">
					<div>
						<input
							type="checkbox"
							checked={allFilteredSelected}
							onChange={toggleAll}
							aria-label={reconciliationMessages.select_all}
							className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
						/>
					</div>
					<div>{reconciliationMessages.column_file}</div>
					<div>{reconciliationMessages.column_sheet}</div>
					<div>{reconciliationMessages.column_object}</div>
					<div>{reconciliationMessages.column_period}</div>
					<div>{reconciliationMessages.column_created}</div>
				</div>

				<div
					ref={parentRef}
					style={{ height: tableHeight, overflow: "auto" }}
					className="contain-strict"
				>
					{filteredFiles.length === 0 ? (
						<div className="flex h-full items-center justify-center text-sm text-slate-500">
							{reconciliationMessages.no_search_results}
						</div>
					) : (
						<div
							style={{
								height: virtualizer.getTotalSize(),
								width: "100%",
								position: "relative",
							}}
						>
							{items.map((virtualRow) => {
								const file = filteredFiles[virtualRow.index];
								return (
									<EligibleFileRow
										key={file.id}
										file={file}
										selected={selectedSet.has(file.id)}
										onToggle={() => toggleFile(file.id)}
										style={{
											position: "absolute",
											top: 0,
											left: 0,
											width: "100%",
											height: ROW_HEIGHT,
											transform: `translateY(${virtualRow.start}px)`,
										}}
									/>
								);
							})}
						</div>
					)}
				</div>
			</div>

			<div className="text-xs text-slate-500">
				{formatMessage(reconciliationMessages.selected_count, {
					selected: visibleSelectedCount,
					total,
				})}
			</div>
		</div>
	);
}

function EligibleFileRow({
	file,
	selected,
	onToggle,
	style,
}: {
	file: FileRef;
	selected: boolean;
	onToggle: () => void;
	style: React.CSSProperties;
}) {
	const displayName = formatFileSheetLabel(
		file.originalName,
		file.sheetName,
		uiMessages.file_sheet_separator,
	);
	const handleKey = (e: React.KeyboardEvent) => {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			onToggle();
		}
	};
	return (
		<button
			type="button"
			onClick={onToggle}
			onKeyDown={handleKey}
			style={style}
			aria-pressed={selected}
			className={`grid grid-cols-[40px_minmax(0,5fr)_7rem_minmax(0,2fr)_120px_160px] items-center gap-x-4 border-b border-slate-100 px-3 text-left text-sm transition-colors cursor-pointer ${
				selected ? "bg-blue-50" : "hover:bg-slate-50"
			}`}
		>
			<span
				aria-hidden="true"
				className={`flex h-4 w-4 items-center justify-center rounded border ${
					selected ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300 bg-white"
				}`}
			>
				{selected && (
					<svg
						className="h-3 w-3"
						fill="none"
						stroke="currentColor"
						strokeWidth="3"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
					</svg>
				)}
			</span>
			<TruncatedText value={file.originalName} className="text-slate-900" />
			{file.sheetName ? (
				<TruncatedText value={file.sheetName} className="text-slate-700" />
			) : (
				<span className="text-slate-600">—</span>
			)}
			{file.objectName ? (
				<TruncatedText value={file.objectName} className="text-slate-600" />
			) : (
				<span className="text-slate-600">—</span>
			)}
			<span className="text-slate-600">{formatPeriod(file.periodMonth, file.periodYear)}</span>
			<span className="text-slate-500">{formatDate(file.createdAt)}</span>
			<span className="sr-only">{`Выбрать ${displayName}`}</span>
		</button>
	);
}
